import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { messageError, messageToast } from '../../utils/define';
import {
    updateAccountAuthInfoAction,
    addAccountAuthInfoAction,
    addAccountInfoAction,
    updateAccountInfoAction,
    getAccountAuthInfoByIdAccountAction,
    getAccountInfoByIdAccountAction,
    resetRoleToAccountAction,
    getByIdAccountAction
} from '../../store/reducers/account';
import { Get } from '../../utils/httpProvider';
import { DONVI_API, CHUCVU_API, DOITUONG_API, CHUYENMON_API, TINH_API, XA_API, BaseGatewayImage, BaseGatewayAuthImage, ROLE_API } from '../../utils/apiUrl';
import Select from 'react-select';
import InputFile from '../../components/input-file/InputFile';
import { acceptImage } from '../../utils/define';
import { getUserSub, isValidUrl } from '../../utils/helper';
import DatePicker from 'react-datepicker';
import { objectAccountGender, objectObjectAccount } from '../../utils/define';
import { userManager } from '@app/utils/oidc';

const schema = yup.object().shape({
    email: yup
        .string()
        .email(messageError.emailMessage)
        .required(messageError.emptyMessage)
});

const SettingsTab = ({ isActive }) => {
    const itemAccount = useSelector((state) => state.account.itemAccount);
    const itemAccountAuthInfo = useSelector((state) => state.account.itemAccountAuthInfo);
    const itemAccountInfo = useSelector((state) => state.account.itemAccountInfo);
    const dispatch = useDispatch();

    // Danh sách tất cả roles từ hệ thống
    const [allRoles, setAllRoles] = useState([]);

    const [donViOptions, setDonViOptions] = useState([]);
    const [chucVuOptions, setChucVuOptions] = useState([]);
    const [doiTuongOptions, setDoiTuongOptions] = useState([]);
    const [chuyenMonOptions, setChuyenMonOptions] = useState([]);

    // Danh sách dropdown cho địa chỉ
    const [provinceOptions, setProvinceOptions] = useState([]);
    // Quê quán (PlaceOfOrigin)
    const [originWardOptions, setOriginWardOptions] = useState([]);
    // Thường trú (HKTT)
    const [hkttWardOptions, setHkttWardOptions] = useState([]);

    // Số CCCD = Username từ OIDC
    const [cccdNumber, setCccdNumber] = useState('');
    // Roles từ OIDC token (vì itemAccount từ Account/GetItem cần quyền admin)
    const [userRoles, setUserRoles] = useState([]);
    useEffect(() => {
        const fetchOidcInfo = async () => {
            const user = await userManager.getUser();
            if (user?.profile?.preferred_username) {
                setCccdNumber(user.profile.preferred_username);
            }
            // Lấy roles từ OIDC token profile
            if (user?.profile?.role) {
                const roles = Array.isArray(user.profile.role)
                    ? user.profile.role
                    : [user.profile.role];
                setUserRoles(roles);
            }
        };
        fetchOidcInfo();
    }, []);

    useEffect(() => {
        const fetchCatalogs = async () => {
            try {
                const donViRes = await Get(DONVI_API.GET_PAGING, null, { PageSize: 1000 });
                if (donViRes?.data) setDonViOptions(donViRes.data.map(x => ({ value: x.id, label: x.tenDonVi })));

                const chucVuRes = await Get(CHUCVU_API.GET_PAGING, null, { PageSize: 1000 });
                if (chucVuRes?.data) setChucVuOptions(chucVuRes.data.map(x => ({ value: x.id, label: x.tenChucVu })));

                const doiTuongRes = await Get(DOITUONG_API.GET_PAGING, null, { PageSize: 1000 });
                if (doiTuongRes?.data) setDoiTuongOptions(doiTuongRes.data.map(x => ({ value: x.id, label: x.tenDoiTuong })));

                const chuyenMonRes = await Get(CHUYENMON_API.GET_PAGING, null, { PageSize: 1000 });
                if (chuyenMonRes?.data) setChuyenMonOptions(chuyenMonRes.data.map(x => ({ value: x.id, label: x.tenChuyenMon })));

                // Fetch all roles for CBNV108/HVNV selection
                const rolesRes = await Get(ROLE_API.GET_ALL);
                if (rolesRes?.data) setAllRoles(rolesRes.data);
            } catch (err) {
                console.error("Error fetching catalogs", err);
            }
        };
        fetchCatalogs();
    }, []);

    // Lấy danh sách Tỉnh/Thành phố khi load form
    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const res = await Get(TINH_API.GET_ALL);
                if (res && Array.isArray(res)) {
                    setProvinceOptions(res.map(item => ({ 
                        value: item.id || item.Id, 
                        label: item.tenTinh || item.TenTinh || item.title 
                    })));
                }
            } catch (err) {
                console.error("Error fetching provinces", err);
            }
        };
        fetchProvinces();
    }, []);

    // Load xã/phường của quê quán khi đã có dữ liệu AccountAuthInfo
    useEffect(() => {
        if (itemAccountAuthInfo?.placeOfOriginProvinceCity && provinceOptions.length > 0) {
            const found = provinceOptions.find(p => p.label === itemAccountAuthInfo.placeOfOriginProvinceCity);
            if (found) {
                fetchWards(found.value, setOriginWardOptions);
            }
        }
    }, [itemAccountAuthInfo?.placeOfOriginProvinceCity, provinceOptions]);

    // Load xã/phường thường trú khi đã có dữ liệu AccountAuthInfo
    useEffect(() => {
        if (itemAccountAuthInfo?.hkttAdressProvinceCity && provinceOptions.length > 0) {
            const found = provinceOptions.find(p => p.label === itemAccountAuthInfo.hkttAdressProvinceCity);
            if (found) {
                fetchWards(found.value, setHkttWardOptions);
            }
        }
    }, [itemAccountAuthInfo?.hkttAdressProvinceCity, provinceOptions]);

    // Hàm fetch xã/phường chung
    const fetchWards = async (provinceId, setWardOptionsFn) => {
        try {
            const res = await Get(XA_API.GET_BY_TINH, provinceId);
            if (res && Array.isArray(res)) {
                setWardOptionsFn(res.map(item => ({ 
                    value: item.id || item.Id, 
                    label: item.tenXa || item.TenXa || item.title 
                })));
            } else {
                setWardOptionsFn([]);
            }
        } catch (err) {
            console.error("Error fetching wards", err);
            setWardOptionsFn([]);
        }
    };

    // Xác định loại tài khoản hiện tại từ roles (lấy từ OIDC token)
    const currentAccountType = useMemo(() => {
        if (userRoles.length > 0) {
            if (userRoles.includes('CAN_BO_NHAN_VIEN')) return 'CBNV108';
            if (userRoles.includes('HOC_VIEN_NGOAI_VIEN')) return 'HVNV';
        }
        return '';
    }, [userRoles]);


    const handleUpdate = async (values, actions) => {
        try {
            let hasError = false;
            let userSub = await getUserSub();
            // Lấy First Name, Last Name (Required field của AuthInfo)
            let firstName = itemAccountAuthInfo?.firstName;
            let lastName = itemAccountAuthInfo?.lastName;
            let middleName = itemAccountAuthInfo?.middleName || '';
            
            if (!firstName || !lastName) {
                if (itemAccount?.fullName) {
                    const parts = itemAccount.fullName.trim().split(' ');
                    lastName = parts[0] || 'New';
                    firstName = parts.length > 1 ? parts[parts.length - 1] : 'User';
                    middleName = parts.length > 2 ? parts.slice(1, parts.length - 1).join(' ') : '';
                } else {
                    firstName = 'User';
                    lastName = 'New';
                }
            }

            // 1. Update AccountAuthInfo (Email, PhoneNumber, Địa chỉ Tỉnh/Xã)
            const authInfoData = {
                ...(itemAccountAuthInfo || {}),
                accountId: userSub,
                firstName: firstName,
                lastName: lastName,
                middleName: middleName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                imagePath: values.imagePath,
                imageThumbnail: values.imageThumbnail,
                placeOfOriginProvinceCity: values.originProvinceName || '',
                placeOfOriginWardCommune: values.originWardName || '',
                hkttAdressProvinceCity: values.hkttProvinceName || '',
                hkttAdressWardCommune: values.hkttWardName || '',
                address: values.address || '',
                objectAccount: values.objectAccount ? values.objectAccount.value : 'khac',
                accountGender: values.accountGender ? values.accountGender.value : 'other',
                dateOfBirth: values.dateOfBirth ? moment(values.dateOfBirth).format('YYYY-MM-DDTHH:mm:ss') : null,
                identityCardNumber: values.identityCardNumber || '',
                issuanceDateOfIdentityCard: values.issuanceDateOfIdentityCard ? moment(values.issuanceDateOfIdentityCard).format('YYYY-MM-DDTHH:mm:ss') : null,
                issuancePlaceOfIdentityCard: values.issuancePlaceOfIdentityCard || '',
                militaryIdentityCardNumber: values.militaryIdentityCardNumber || '',
                militaryIssuanceDateOfIdentityCard: values.militaryIssuanceDateOfIdentityCard ? moment(values.militaryIssuanceDateOfIdentityCard).format('YYYY-MM-DDTHH:mm:ss') : null,
                militaryIssuancePlaceOfIdentityCard: values.militaryIssuancePlaceOfIdentityCard || ''
            };

            if (itemAccountAuthInfo) {
                // Nghia fix vì khi khác hoặc nữ = 0 nó thấy trùng chuỗi rỗng nên không lưu được trong database, phải đổi sang nó thấy khác với 0 hiện tại nên nó mới lưu được
                let tempAuthInfoData = { ...authInfoData };
                let needDoubleUpdate = false;
                
                if (authInfoData.objectAccount === 'khac') {
                    tempAuthInfoData.objectAccount = 'quanNhan';
                    needDoubleUpdate = true;
                }
                if (authInfoData.accountGender === 'female') {
                    tempAuthInfoData.accountGender = 'male';
                    needDoubleUpdate = true;
                }
                
                if (needDoubleUpdate) {
                    await dispatch(updateAccountAuthInfoAction({ id: itemAccountAuthInfo.id, data: tempAuthInfoData }));
                }
                // đến đây
                const res1 = await dispatch(updateAccountAuthInfoAction({ id: itemAccountAuthInfo.id, data: authInfoData }));
                if (res1.payload?.errors) hasError = true;
            } else {
                const res1 = await dispatch(addAccountAuthInfoAction(authInfoData));
                if (res1.payload?.errors) hasError = true;
            }

            // 2. Update AccountInfo (DonVi, ChucVu, DoiTuong, ChuyenMon)
            const infoId = itemAccountInfo?.id || null;
            const accountInfoData = {
                accountId: userSub,
                doiTuongId: values.doiTuong?.value || null,
                chucDanhChuyenMonId: values.chucDanhChuyenMon?.value || null,
                lstDonViId: values.accountType === 'CBNV108' ? (values.lstDonVi?.map(x => x.value) || []) : [],
                lstChucVuId: values.lstChucVu?.map(x => x.value) || [],
                lstChuyenMonId: values.lstChuyenMon?.map(x => x.value) || [],
                donViNgoaiVien: values.accountType === 'HVNV' ? (values.donViNgoaiVien || '') : ''
            };

            if (infoId) {
                const res2 = await dispatch(updateAccountInfoAction({ id: infoId, data: accountInfoData }));
                if (res2.payload?.errors) hasError = true;
            } else {
                const res2 = await dispatch(addAccountInfoAction(accountInfoData));
                if (res2.payload?.errors) hasError = true;
            }

            // 3. Update Account Type (CBNV108 / HVNV) via Role
            if (values.accountType && values.accountType !== currentAccountType) {
                const roleName = values.accountType === 'CBNV108' ? 'CAN_BO_NHAN_VIEN' : 'HOC_VIEN_NGOAI_VIEN';
                const targetRole = allRoles.find(r => r.name === roleName);
                if (targetRole) {
                    const roleRes = await dispatch(resetRoleToAccountAction({
                        lstRoleOfPhanMem: [{ id: targetRole.id, name: targetRole.name }]
                    }));
                    if (roleRes.payload?.errors) {
                        hasError = true;
                    } else {
                        // Cập nhật userRoles local state để radio tick đúng ngay
                        setUserRoles(prev => {
                            const filtered = prev.filter(r => r !== 'CAN_BO_NHAN_VIEN' && r !== 'HOC_VIEN_NGOAI_VIEN');
                            return [...filtered, roleName];
                        });
                        // Reload account data to refresh roles on sidebar
                        if (userSub) {
                            dispatch(getByIdAccountAction(userSub));
                        }
                    }
                } else {
                    console.error('Không tìm thấy role:', roleName);
                    hasError = true;
                }
            }

            if (!hasError) {
                // Reload dữ liệu bên trái
                if (userSub) {
                    dispatch(getAccountAuthInfoByIdAccountAction(userSub));
                    dispatch(getAccountInfoByIdAccountAction(userSub));
                }
                toast.success(messageToast.messageToastUpdateSuccess);
            } else {
                toast.error("Có lỗi xảy ra khi cập nhật thông tin.");
            }
        } catch (error) {
            toast.error(messageToast.messageToastTryCatch);
        }
    };

    return (
        <div className={`tab-pane ${isActive ? 'active' : ''}`}>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values, actions) => handleUpdate(values, actions)}
                    initialValues={{
                        email: itemAccountAuthInfo?.email || itemAccount?.email || '',
                        phoneNumber: itemAccountAuthInfo?.phoneNumber || '',
                        lstDonVi: itemAccountInfo?.donVis?.map(d => ({ value: d.id, label: d.tenDonVi })) || [],
                        donViNgoaiVien: itemAccountInfo?.donViNgoaiVien || '',
                        lstChucVu: itemAccountInfo?.chucVus?.map(c => ({ value: c.id, label: c.tenChucVu })) || [],
                        doiTuong: itemAccountInfo?.doiTuong ? { value: itemAccountInfo.doiTuong.id, label: itemAccountInfo.doiTuong.tenDoiTuong } : null,
                        chucDanhChuyenMon: itemAccountInfo?.chucDanhChuyenMon ? { value: itemAccountInfo.chucDanhChuyenMon.id, label: itemAccountInfo.chucDanhChuyenMon.tenDoiTuong } : null,
                        lstChuyenMon: itemAccountInfo?.chuyenMons?.map(c => ({ value: c.id, label: c.tenChuyenMon })) || [],
                        // Quê quán
                        originProvinceName: itemAccountAuthInfo?.placeOfOriginProvinceCity || '',
                        originWardName: itemAccountAuthInfo?.placeOfOriginWardCommune || '',
                        // Thường trú
                        hkttProvinceName: itemAccountAuthInfo?.hkttAdressProvinceCity || '',
                        hkttWardName: itemAccountAuthInfo?.hkttAdressWardCommune || '',
                        address: itemAccountAuthInfo?.address || '',
                        imagePath: itemAccountAuthInfo?.imagePath || '',
                        imageThumbnail: itemAccountAuthInfo?.imageThumbnail || '',
                        objectAccount: itemAccountAuthInfo?.objectAccount != null ? objectObjectAccount.find(x => x.value === itemAccountAuthInfo.objectAccount) || null : null,
                        accountGender: itemAccountAuthInfo?.accountGender != null ? objectAccountGender.find(x => x.value === itemAccountAuthInfo.accountGender) || null : null,
                        dateOfBirth: itemAccountAuthInfo?.dateOfBirth ? new Date(itemAccountAuthInfo.dateOfBirth) : null,
                        identityCardNumber: cccdNumber || '',
                        issuanceDateOfIdentityCard: itemAccountAuthInfo?.issuanceDateOfIdentityCard ? new Date(itemAccountAuthInfo.issuanceDateOfIdentityCard) : null,
                        issuancePlaceOfIdentityCard: itemAccountAuthInfo?.issuancePlaceOfIdentityCard || '',
                        militaryIdentityCardNumber: itemAccountAuthInfo?.militaryIdentityCardNumber || '',
                        militaryIssuanceDateOfIdentityCard: itemAccountAuthInfo?.militaryIssuanceDateOfIdentityCard ? new Date(itemAccountAuthInfo.militaryIssuanceDateOfIdentityCard) : null,
                        militaryIssuancePlaceOfIdentityCard: itemAccountAuthInfo?.militaryIssuancePlaceOfIdentityCard || '',
                        accountType: currentAccountType
                    }}
                >
                    {({handleSubmit, handleChange, setFieldValue, values, errors}) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            
                            <div className="bg-light p-4 rounded mb-4 border">
                                <Row>
                                    {/* ==================== ẢNH ĐẠI DIỆN ==================== */}
                                    <Col md={6} className="border-md-right pr-md-4 mb-4 mb-md-0 pb-4 pb-md-0 d-flex align-items-center justify-content-center">
                                        <Form.Group className="mb-0 d-flex flex-column flex-xl-row align-items-center justify-content-center w-100">
                                            <Form.Label className="font-weight-bold text-dark mb-3 mb-xl-0 mr-xl-5" style={{ whiteSpace: 'nowrap' }}>
                                                Ảnh đại diện
                                            </Form.Label>
                                            <div className="d-flex justify-content-center">
                                                <InputFile
                                                    uploadBaseGatewayUrl={BaseGatewayAuthImage}
                                                    enableReinitialize={true}
                                                    values={
                                                        values.imageThumbnail
                                                            ? [
                                                                isValidUrl(values.imageThumbnail)
                                                                    ? values.imageThumbnail
                                                                    : BaseGatewayAuthImage + '/' + values.imageThumbnail.replace(/^\//, '')
                                                            ]
                                                            : []
                                                    }
                                                    name="imageThumbnail"
                                                    accept={acceptImage}
                                                    labelImage="Chọn ảnh đại diện"
                                                    onChange={(files) => {
                                                        if (files.length > 0) {
                                                            setFieldValue('imagePath', files[0].filePath, true);
                                                            setFieldValue('imageThumbnail', files[0].thumbPath, true);
                                                        } else {
                                                            setFieldValue('imagePath', '', true);
                                                            setFieldValue('imageThumbnail', '', true);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    {/* ==================== LOẠI TÀI KHOẢN ==================== */}
                                    <Col md={6} className="pl-md-4">
                                        <Form.Group className="mb-0">
                                            <h6 className="text-primary font-weight-bold mb-4 border-bottom pb-3">
                                                <i className="fas fa-id-badge mr-2" style={{ fontSize: '1.2em' }}></i> 
                                                Bạn có phải CBNV Bệnh viện TWQĐ 108?
                                            </h6>
                                            <div className="d-flex flex-column" style={{ gap: '16px' }}>
                                                <Form.Check
                                                    type="radio"
                                                    id="accountType-CBNV108"
                                                    name="accountType"
                                                    label={
                                                        <span style={{ fontWeight: values.accountType === 'CBNV108' ? 600 : 400 }}>
                                                            <i className="fas fa-user-tie mr-2 text-success"></i>
                                                            CBNV108 <small className="text-muted">(Cán bộ nhân viên)</small>
                                                        </span>
                                                    }
                                                    value="CBNV108"
                                                    checked={values.accountType === 'CBNV108'}
                                                    onChange={() => setFieldValue('accountType', 'CBNV108')}
                                                    className="mb-0"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    id="accountType-HVNV"
                                                    name="accountType"
                                                    label={
                                                        <span style={{ fontWeight: values.accountType === 'HVNV' ? 600 : 400 }}>
                                                            <i className="fas fa-user-graduate mr-2 text-info"></i>
                                                            HVNV <small className="text-muted">(Học viên ngoài viện)</small>
                                                        </span>
                                                    }
                                                    value="HVNV"
                                                    checked={values.accountType === 'HVNV'}
                                                    onChange={() => setFieldValue('accountType', 'HVNV')}
                                                    className="mb-0"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            {values.accountType === 'HVNV' && currentAccountType === 'CBNV108' && (
                                                <div className="mt-3 p-2 border-left border-warning bg-white rounded" style={{ borderLeftWidth: '4px' }}>
                                                    <small className="text-warning">
                                                        <i className="fas fa-exclamation-triangle mr-1"></i>
                                                        Lưu ý: Khi chuyển sang HVNV, tất cả các quyền hiện tại sẽ bị xóa và chỉ còn quyền Học viên ngoài viện.
                                                    </small>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                            
                            {/* ==================== THÔNG TIN CÁ NHÂN ==================== */}
                            <div className="bg-light p-4 rounded mb-4 border">
                                <h6 className="text-primary font-weight-bold mb-4 border-bottom pb-3">
                                    <i className="fas fa-user-circle mr-2" style={{ fontSize: '1.2em' }}></i> 
                                    Thông tin cá nhân
                                </h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Email <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nhập email..."
                                                name="email"
                                                value={values.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Số điện thoại</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nhập số điện thoại..."
                                                name="phoneNumber"
                                                value={values.phoneNumber}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Đối tượng</Form.Label>
                                            <Select
                                                isClearable
                                                options={objectObjectAccount}
                                                value={values.objectAccount}
                                                onChange={(val) => setFieldValue('objectAccount', val)}
                                                placeholder="Chọn đối tượng..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Giới tính</Form.Label>
                                            <Select
                                                isClearable
                                                options={objectAccountGender}
                                                value={values.accountGender}
                                                onChange={(val) => setFieldValue('accountGender', val)}
                                                placeholder="Chọn giới tính..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Ngày sinh</Form.Label>
                                            <DatePicker
                                                className="form-control shadow-none"
                                                wrapperClassName="w-100 d-block"
                                                selected={values.dateOfBirth}
                                                onChange={(date) => setFieldValue('dateOfBirth', date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Chọn ngày sinh"
                                                isClearable
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Số CCCD</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Số CCCD"
                                                name="identityCardNumber"
                                                value={values.identityCardNumber}
                                                disabled
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Ngày cấp CCCD</Form.Label>
                                            <DatePicker
                                                className="form-control shadow-none"
                                                wrapperClassName="w-100 d-block"
                                                selected={values.issuanceDateOfIdentityCard}
                                                onChange={(date) => setFieldValue('issuanceDateOfIdentityCard', date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Chọn ngày cấp"
                                                isClearable
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Nơi cấp CCCD</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nơi cấp"
                                                name="issuancePlaceOfIdentityCard"
                                                value={values.issuancePlaceOfIdentityCard}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {values.objectAccount?.value === 'quanNhan' && (
                                    <Row className="mt-3 p-3 bg-white rounded border border-light align-items-start">
                                        <Col md={4}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="font-weight-bold text-muted small">Số CM Quân đội</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Số CM Quân đội"
                                                    name="militaryIdentityCardNumber"
                                                    value={values.militaryIdentityCardNumber}
                                                    onChange={handleChange}
                                                    className="shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="font-weight-bold text-muted small">Ngày cấp CM Quân Đội</Form.Label>
                                                <DatePicker
                                                    className="form-control shadow-none"
                                                    wrapperClassName="w-100 d-block"
                                                    selected={values.militaryIssuanceDateOfIdentityCard}
                                                    onChange={(date) => setFieldValue('militaryIssuanceDateOfIdentityCard', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="Chọn ngày cấp"
                                                    isClearable
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="font-weight-bold text-muted small">Nơi cấp CM Quân Đội</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Nơi cấp"
                                                    name="militaryIssuancePlaceOfIdentityCard"
                                                    value={values.militaryIssuancePlaceOfIdentityCard}
                                                    onChange={handleChange}
                                                    className="shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            {/* ==================== ĐƠN VỊ CÔNG TÁC ==================== */}
                            <div className="bg-light p-4 rounded mb-4 border">
                                <h6 className="text-primary font-weight-bold mb-2 border-bottom pb-3">
                                    <i className="fas fa-briefcase mr-2" style={{ fontSize: '1.2em' }}></i> 
                                    Đơn vị công tác
                                </h6>
                                <p className="text-danger font-italic small mb-4">
                                    * Lưu ý: Bạn phải chọn đúng bạn là Cán bộ nhân viên Bệnh viện TWQĐ 108 hay Học viên ngoài viện (Ở đầu trang)
                                </p>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            {values.accountType === 'CBNV108' ? (
                                                <>
                                                    <Form.Label className="font-weight-bold text-muted small">Đơn vị</Form.Label>
                                                    <Select
                                                        isMulti
                                                        options={donViOptions}
                                                        value={values.lstDonVi}
                                                        onChange={(val) => setFieldValue('lstDonVi', val)}
                                                        placeholder="Chọn đơn vị..."
                                                        className="shadow-none"
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Form.Label className="font-weight-bold text-muted small">Đơn vị ngoài viện</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Nhập tên đơn vị ngoài viện..."
                                                        name="donViNgoaiVien"
                                                        value={values.donViNgoaiVien}
                                                        onChange={handleChange}
                                                        className="shadow-none"
                                                    />
                                                </>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Chức vụ</Form.Label>
                                            <Select
                                                isMulti
                                                options={chucVuOptions}
                                                value={values.lstChucVu}
                                                onChange={(val) => setFieldValue('lstChucVu', val)}
                                                placeholder="Chọn chức vụ..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Chức danh chuyên môn</Form.Label>
                                            <Select
                                                isClearable
                                                options={doiTuongOptions}
                                                value={values.chucDanhChuyenMon}
                                                onChange={(val) => setFieldValue('chucDanhChuyenMon', val)}
                                                placeholder="Chọn chức danh chuyên môn trong GPHN..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Chức danh đang hoạt động</Form.Label>
                                            <Select
                                                isClearable
                                                options={doiTuongOptions}
                                                value={values.doiTuong}
                                                onChange={(val) => setFieldValue('doiTuong', val)}
                                                placeholder="Chọn chức danh đang hoạt động tại BV..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            {/* ==================== QUÊ QUÁN ==================== */}
                            <div className="bg-light p-4 rounded mb-4 border">
                                <h6 className="text-primary font-weight-bold mb-4 border-bottom pb-3">
                                    <i className="fas fa-map-marker-alt mr-2" style={{ fontSize: '1.2em' }}></i> 
                                    Quê quán
                                </h6>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Tỉnh / Thành phố</Form.Label>
                                            <Select
                                                isClearable
                                                options={provinceOptions}
                                                value={provinceOptions.find(p => p.label === values.originProvinceName) || (values.originProvinceName ? { value: values.originProvinceName, label: values.originProvinceName } : null)}
                                                onChange={(val) => {
                                                    setFieldValue('originProvinceName', val ? val.label : '');
                                                    setFieldValue('originWardName', '');
                                                    setOriginWardOptions([]);
                                                    if (val) fetchWards(val.value, setOriginWardOptions);
                                                }}
                                                placeholder="Chọn tỉnh / thành phố quê quán..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Xã / Phường</Form.Label>
                                            <Select
                                                isClearable
                                                options={originWardOptions}
                                                value={originWardOptions.find(w => w.label === values.originWardName) || (values.originWardName ? { value: values.originWardName, label: values.originWardName } : null)}
                                                onChange={(val) => setFieldValue('originWardName', val ? val.label : '')}
                                                placeholder={values.originProvinceName ? "Chọn xã / phường quê quán..." : "Vui lòng chọn tỉnh/thành phố trước"}
                                                isDisabled={!values.originProvinceName}
                                                noOptionsMessage={() => values.originProvinceName ? "Không tìm thấy xã / phường" : "Vui lòng chọn tỉnh/thành phố trước"}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            {/* ==================== THƯỜNG TRÚ ==================== */}
                            <div className="bg-light p-4 rounded mb-4 border">
                                <h6 className="text-primary font-weight-bold mb-4 border-bottom pb-3">
                                    <i className="fas fa-home mr-2" style={{ fontSize: '1.2em' }}></i> 
                                    Hộ khẩu thường trú
                                </h6>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Tỉnh / Thành phố</Form.Label>
                                            <Select
                                                isClearable
                                                options={provinceOptions}
                                                value={provinceOptions.find(p => p.label === values.hkttProvinceName) || (values.hkttProvinceName ? { value: values.hkttProvinceName, label: values.hkttProvinceName } : null)}
                                                onChange={(val) => {
                                                    setFieldValue('hkttProvinceName', val ? val.label : '');
                                                    setFieldValue('hkttWardName', '');
                                                    setHkttWardOptions([]);
                                                    if (val) fetchWards(val.value, setHkttWardOptions);
                                                }}
                                                placeholder="Chọn tỉnh / thành phố thường trú..."
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold text-muted small">Xã / Phường</Form.Label>
                                            <Select
                                                isClearable
                                                options={hkttWardOptions}
                                                value={hkttWardOptions.find(w => w.label === values.hkttWardName) || (values.hkttWardName ? { value: values.hkttWardName, label: values.hkttWardName } : null)}
                                                onChange={(val) => setFieldValue('hkttWardName', val ? val.label : '')}
                                                placeholder={values.hkttProvinceName ? "Chọn xã / phường thường trú..." : "Vui lòng chọn tỉnh/thành phố trước"}
                                                isDisabled={!values.hkttProvinceName}
                                                noOptionsMessage={() => values.hkttProvinceName ? "Không tìm thấy xã / phường" : "Vui lòng chọn tỉnh/thành phố trước"}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-0">
                                            <Form.Label className="font-weight-bold text-muted small">Địa chỉ chi tiết</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nhập số nhà, tên đường, ..."
                                                name="address"
                                                value={values.address}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <Form.Group as={Row} className="mb-3">
                                <Col className="text-center">
                                    <Button type="submit" theme="danger">
                                        Cập nhật
                                    </Button>
                                </Col>
                            </Form.Group>

                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </div>
    );
};

export default SettingsTab;
